/**
 * InlineRetry — a tiny shared "…failed, try again" affordance (Task 12 / C4).
 *
 * Used next to catalog-driven <select>s in the Wizard and TemplateEditor: when a
 * catalog list fetch fails the dropdown swaps to a single disabled "Couldn't load"
 * option and THIS control offers a one-click retry, so a swallowed fetch failure is
 * visible and recoverable instead of leaving a silently-empty dropdown. Tokens-only
 * styling; an optional leading message lets it double as a standalone "couldn't load X"
 * line (e.g. the editor's appearance options).
 *
 * R10 (WS-Z7): the retry is a `.btn--sm.btn--ghost`. `.inline-retry__btn` used to be its own
 * button vocabulary — its own padding, radius, border, background and focus ring — which is
 * how a recovery control ended up smaller than every other control beside it.
 */
import { Icon } from "./Icon.js";
import "./ui.css";

export interface InlineRetryProps {
  /** Re-run the failed fetch. */
  onRetry: () => void;
  /** Optional leading text (used when there is no select to carry the message). */
  message?: string;
  /** Button text. */
  label?: string;
  /** data-testid for the retry button (per field group). */
  testId?: string;
  className?: string;
  /**
   * The retry cannot succeed YET (a precondition is still loading). The button is disabled and
   * carries `disabledReason` as its tooltip, rather than being pressable and doing nothing —
   * WS-ERR F-6, where pressing Retry before the case image had decoded left the caller's
   * "Restoring…" line on screen over an editor that had refused the work.
   */
  disabled?: boolean;
  /** Why the button is disabled. Rendered as the button's `title`. */
  disabledReason?: string;
}

export function InlineRetry({
  onRetry,
  message,
  label = "Retry",
  testId = "catalog-retry",
  className,
  disabled = false,
  disabledReason,
}: InlineRetryProps) {
  return (
    <span className={`inline-retry${className ? ` ${className}` : ""}`} role="status">
      {message && <span className="inline-retry__msg">{message}</span>}
      <button
        type="button"
        className="btn btn--sm btn--ghost inline-retry__btn"
        onClick={onRetry}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        data-testid={testId}
      >
        <span className="inline-retry__icon" aria-hidden="true"><Icon name="refresh" size={16} /></span>
        {label}
      </button>
    </span>
  );
}
